/**
 * PAC-1388 — the Packhouse Edition's own sign-up page (/signup).
 *
 * A visitor with no account fills in the company and themselves; the backend
 * creates their Auth0 login and emails an Auth0 set-password link styled with
 * our branding. They never type a password on our page — Auth0 hosts that
 * step. Their first sign-in creates the company from what they typed here
 * (`completePendingCompanySignup`, called by the session store).
 *
 * Only on a WMS front door: `selfServeSignup` on AND `signupForm === "COMPANY"`
 * (the backend sends COMPANY only when the app carries a wmsEdition). Boer's
 * farm flow — Auth0's own sign-up, then the farm form — is untouched.
 */
import { apiRequest } from "@/auth/session";
import type { ResolveTenantAppResponse } from "@/tenant/types";

export const SIGNUP_PATH = "/signup";

/** Does this front door offer the company sign-up page? */
export function offersCompanySignup(tenant: Pick<ResolveTenantAppResponse, "selfServeSignup" | "signupForm"> | null): boolean {
  return tenant?.selfServeSignup === true && tenant?.signupForm === "COMPANY";
}

export function isSignupPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, "") === SIGNUP_PATH;
}

/**
 * In-app navigation between "/" and "/signup" without a reload. App.vue tracks
 * the path through `popstate`, which `pushState` does not fire on its own.
 */
export function goTo(path: string): void {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export interface CompanySignupRequest {
  tenantAppSlug: string;
  person: { firstName: string; lastName: string; email: string; mobile?: string };
  company: {
    legalName: string;
    registrationNumber?: string;
    vatNumber?: string;
    baseCurrency: string;
    countryCode: string;
  };
  /** Honeypot — a hidden field people never fill. */
  website?: string;
}

/** `POST /packhouse/signup/request` — public: no bearer, no org token. */
export async function requestCompanySignup(body: CompanySignupRequest): Promise<{ accepted: boolean; message: string }> {
  return apiRequest("/packhouse/signup/request", { method: "POST", body });
}

export interface PendingSignupCompletion {
  outcome: "CREATED" | "CLAIMED" | "NEEDS_REVIEW" | "NO_PENDING_SIGNUP";
  organisationId: number | null;
  organisationName: string | null;
  reference: string | null;
}

/**
 * `POST /packhouse/signup/complete` — the first verified sign-in. Sends the
 * Auth0 bearer (the caller has no organisation yet); NO_PENDING_SIGNUP means
 * they never used the page, and the in-app company form takes over.
 */
export async function completePendingCompanySignup(tenantAppSlug: string, bearer: string): Promise<PendingSignupCompletion> {
  return apiRequest("/packhouse/signup/complete", { method: "POST", bearer, body: { tenantAppSlug } });
}
